import React from 'react';
import { useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import { Icon, message, Modal } from '@/components';
import { RootStore } from '@/models';
import ActionBtn from '../ActionBtn';
import styles from './index.less';
import classNames from 'classnames';

interface Props extends Partial<Pick<RootStore, 'rtc' | 'user' | 'meeting'>> {
  RTCInstance?: RootStore['rtc'];
  user?: RootStore['user'];
  meeting?: RootStore['meeting'];
}
const ShareScreenBtn: React.FC<Props> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    user: store.user,
    meeting: store.meeting,
  };
})(
  observer((props) => {
    const { RTCInstance, user, meeting } = props;
    const { formatMessage } = useIntl();

    const openShareScreen = () => {
      RTCInstance?.setIsShareScreen(true).catch((err) => {
        RTCInstance?.setIsShareScreen(false).catch((e) => {
          console.error('failed to close screen sharing.', e);
        });
        if (err.status !== undefined) {
          let text: string;
          switch (err.status) {
            case 530001:
              text = formatMessage({
                id: 'FAILED_TO_SCREEN_SHARING_BECAUSE_OF_WHITEBOARD',
              });
              break;
            case 530002:
              text = formatMessage({
                id: 'FAILED_TO_SCREEN_SHARING_BECAUSE_OF_DUPLICATED',
              });
              break;
            default:
              text = err.message;
              break;
          }
          message.info({
            content: text,
            noIcon: true,
          });
        } else if (String(err).indexOf('mac os system') !== -1) {
          Modal.info({
            title: formatMessage({ id: 'NOT_SHARE_SCREEN' }),
            content: formatMessage({ id: 'SYSTEM_PREFERENCES_RMISSION' }),
            okText: formatMessage({ id: 'CONFIRM' }),
          });
        } else if (err instanceof Error) {
          console.error('failed to switch screen. ', err.message);
        }
      });
    };
    const stopScreenClick = React.useCallback(async () => {
      Modal.confirm({
        focusTriggerAfterClose: false,
        title: formatMessage({ id: 'ARE_YOU_SURE_YOU_WANT_TO_STOP_SCREEN_SHARING' }),
        okText: formatMessage({ id: 'STOP' }),
        cancelText: formatMessage({ id: 'CANCEL' }),
        okButtonProps: {
          className: `popo-meeting-button`,
          danger: true,
        },
        onOk: async () => {
          await RTCInstance?.setIsShareScreen(false);
        },
      });
    }, [RTCInstance]);

    const switchScreen = React.useCallback(async () => {
      if (RTCInstance?.breakLineReconnecting) {
        return;
      }
      if (meeting?.whiteboardUid) {
        message.info({
          content: formatMessage({ id: 'FAILED_TO_SCREEN_SHARING_BECAUSE_OF_WHITEBOARD' }),
          noIcon: true,
        });
        return;
      }
      if (meeting?.codeEditorEnabled) {
        message.info({
          content: formatMessage({ id: 'FAILED_TO_SCREEN_SHARING_BECAUSE_OF_CODEEDITOR' }),
          noIcon: true,
        });
        return;
      }

      if (meeting?.screenUserInfo?.yxUidStr && meeting?.screenUserInfo?.yxUidStr !== user?.yxuid) {
        Modal.confirm({
          title: formatMessage({ id: 'replaceShare' }),
          okText: formatMessage({ id: 'confirm' }),
          cancelText: formatMessage({ id: 'CANCEL' }),
          onOk: (close) => {
            openShareScreen();
            close();
          },
          onCancel: () => {
            return;
          },
        });
      } else {
        if (RTCInstance!.isShareScreen) {
          stopScreenClick();
        } else {
          openShareScreen();
        }
      }
    }, [RTCInstance, formatMessage, stopScreenClick, meeting, user]);

    return (
      <ActionBtn
        tooltip={RTCInstance?.isShareScreen ? '' : formatMessage({ id: 'enableScreenSharing' })}
        title={formatMessage({ id: 'inShareing' })}
        className={classNames(styles.shareScreenBtn, {
          [styles.on]: meeting?.screenUid === user?.mid,
          [styles.disable]:
            meeting?.whiteboardUid ||
            meeting?.codeEditorEnabled ||
            RTCInstance?.breakLineReconnecting,
        })}
        onClick={switchScreen}
      >
        <Icon className={styles.icon} name="icon-a-Mirrorshare" mode="symbol" size="24" />
      </ActionBtn>
    );
  })
);

export default ShareScreenBtn;
